<template>
    <div
        v-if="!isMobile"
        class="sticky top-0 z-50 bg-white w-full h-[70px]"
    >
        <placement-header-alt
            :placement="backOptions?.[0]"
            :placementData="backOptions?.[0]"
            :index="0"
            :has-error="hasEmptySelectionErrorState"
            :hide-combine="true"
        >
            <template #headerSelected>
                <div class="flex-1 text-[14px]">
                    <div v-if="noCustomizationSelected" class="text-right">
                        {{ $t('STOCK_PAINTFILL') }}
                    </div>
                    <div v-else-if="nothingSelected" class="text-right">
                        <double-dash />
                    </div>
                    <div v-else class="text-right whitespace-nowrap">
                        {{ $t('PAINTFILL') }}
                        <span class="text-gray-500 mx-1">|</span>
                        <a href="#" @click.prevent.stop="navigateToPaintfill"
                            class="underline font-oceanside"
                        >
                            {{ $t('VIEW_ALL_COLOR_LOCATIONS') }}
                        </a>
                    </div>
                </div>
            </template>
        </placement-header-alt>
        <step-options-color-controls :is-active="true" />
    </div>
    <div class="relative">
        <div class="sticky top-0 z-50">
            <mobile-product-renderer v-if="isMobile" />
            <step-options-color-controls
                :is-active="true"
                v-if="isMobile"
            />
        </div>
        <step-options-initials-buttons
            class="relative px-[20px] lg:px-[40px] mt-[24px] lg:mt-[68px]"
            v-if="backOptions?.length > 0 && backOption"
            :has-add-remove-button="hasAddRemoveButton"
            :has-local-error="hasEmptySelectionErrorState"
            :main-placement="backOptions?.[0]"
            :option="backOption"
            @unselect="openRemoveConfirmationModal"
            @select="selectActual"
            :index="0"
            :scroll-to-offset="98"
            :scroll-to-timeout="600"
            :has-error-state="hasEmptySelectionErrorState"
            :all-components="allComponents"
            :has-image="shouldShowImage"
            background-size="auto 100%"
        >
            <transition name="cz3__fade">
                <div
                    v-if="isActive"
                    class="mt-[24px] mb-[44px]"
                >
                    <step-options-back-list
                        :visible-placements="visiblePlacements"
                        :has-color-error-state="hasPaintfillColorErrorState"
                        @updateColorIsSelected="handleUpdateColorIsSelected($event)"
                    />
                </div>
            </transition>
        </step-options-initials-buttons>

        <div class="px-[20px] lg:px-[40px]" v-if="!isAnyPaintfillSelected">
            <div class="group"
                id="step-options-back-no-customization-button"
                @click="handleSelectNoCustomization"
            >
                <div
                    :class="[
                        'w-full h-[128px] xs:h-36 bg-gray-F6F6F6 border border-b-0 cursor-pointer bg-no-repeat',
                        'group-hover:border-black group-hover:border-b-0',
                        noCustomizationSelected ? 'border-black' : (hasEmptySelectionErrorState ? 'border-red' : 'border-gray-DFDFDF'),
                    ]"
                    :style="{
                        backgroundImage: `url(&quot;${customizerStore.makeImageUrl(noCustomizationImage)}&quot;)`,
                        backgroundPosition: 'center',
                        backgroundSize: 'auto 100%',
                        backgroundRepeat: 'no-repeat',
                    }"
                >
                </div>
                <button
                    type="button"
                    :class="[
                        noCustomizationSelected ? 'border-black' : '',
                        hasEmptySelectionErrorState ? 'border-red' : noCustomizationSelected ? 'border-black' : 'border-gray-DFDFDF',
                        'border-b border-l border-r h-12 w-full flex items-center justify-between font-sans p-4 mb-[32px] text-[16px] tracking-[0.56px]',
                        'group-hover:border-black',
                        'group-focus:border-l group-focus:border-r group-focus:border-t',
                        'group-focus:outline-none group-focus:ring-1 group-focus:ring-black group-focus:border-black ring-offset-[-2px]'
                    ]"
                >
                    <div
                        class="flex w-full justify-between items-center"
                    >
                        {{ $t('NO_CUSTOMIZATION') }}
                        <black-white-button
                            class="w-[90px] rounded-full font-semibold h-[34px] flex items-center justify-center"
                            :is-selected="!noCustomizationSelected"
                        >
                            {{ noCustomizationSelected ? $t('REMOVE') : $t('ADD') }}
                        </black-white-button>
                    </div>
                </button>
            </div>
        </div>

        <teleport to="#cz3" v-if="showRemoveConfirmationModal">
            <base-modal-confirm
                @modalClose="closeRemoveConfirmationModal"
                @confirmAndExit="handleRemovePaintfillSelection"
                :show-modal="showRemoveConfirmationModal"
                :modal-copy="$t('RESET_PAINTFILL_CONFIRMATION_COPY')"
                :modal-heading="$t('RESET_PAINTFILL_CONFIRMATION')"
                :cta-copy="$t('REMOVE')"
                :show-cancel-button="true"
            />
        </teleport>
    </div>
</template>

<script>
    import allStores from '../stores/all';
    import StepOptionsInitialsButtons from './buttons/StepOptionsInitialsButtons.vue';
    import StepOptionsBackList from './StepOptionsBackList.vue';
    import BlackWhiteButton from './buttons/BlackWhiteButton.vue';
    import PlacementHeaderAlt from './PlacementHeaderAlt.vue';
    import StepOptionsColorControls from './StepOptionsColorControls.vue';
    import DoubleDash from './DoubleDash.vue';
    import MobileProductRenderer from './MobileProductRenderer.vue';
    import BaseModalConfirm from './BaseModalConfirm.vue';
    import { getImageSuffix } from '../helpers/getimage';

    export default {
        mixins: [allStores],

        components: {
            StepOptionsInitialsButtons,
            StepOptionsBackList,
            BlackWhiteButton,
            PlacementHeaderAlt,
            StepOptionsColorControls,
            DoubleDash,
            MobileProductRenderer,
            BaseModalConfirm,
        },

        name: 'StepOptionsBack',

        data() {
            return {
                showRemoveConfirmationModal: false,
            };
        },

        props: {
            itemData: {
                type: Object,
                required: true,
            },
            isDirty: {
                type: Boolean,
                default: false,
            },
            hasAddRemoveButton: {
                type: Boolean,
                default: true,
            },
        },

        computed: {
            paintfillSelected() {
                return this.customizerStore.isAnyPaintfillSelected('Back');
            },

            nothingSelected() {
                return !this.noCustomizationSelected && !this.paintfillSelected;
            },

            noCustomizationSelected() {
                return this.customizerStore.isBackNoCustomizationSelected();
            },

            isMobile() {
                return this.appStore?.isMobile;
            },

            shouldShowColorControls() {
                return this.customizerStore.shouldShowColorControls('back');
            },

            shouldShowImage() {
                if (this.isMobile) {
                    return !this.isActive;
                }
                return true;
            },

            isActive() {
                return this.customizerStore.isBackSectionActive('back');
            },

            viewController() {
                return this.customizerStore.viewController;
            },

            mainPlacement() {
                return this.viewController.findPlacement('PutterBackPaintfill');
            },

            selectedComponent() {
                if (!this.mainPlacement) {
                    return null;
                }

                return this.viewController.selectedAtPlacement(this.mainPlacement.code);
            },

            backOption() {
                const component = this.mainPlacement?.components?.find((x) => !x.placeholder);

                if (!component) {
                    return null;
                }

                return {
                    selected: this.isActive,
                    label: 'Paintfill',
                    value: component.code,
                    raw: {
                        ...component,
                        custom: {
                            ...(component.custom || {}),
                            ...{
                                'v-thumbnail-asset': 'PutterBackPaintfill-Selected',
                            },
                        },
                    },
                };
            },

            backOptions() {
                if (!this.mainPlacement) {
                    return [];
                }

                const selected = this.viewController.selectedAtPlacement(this.mainPlacement.code);

                return [{
                    selectedValue: selected ? selected.code : null,
                    groupType: this.mainPlacement.custom?.['v-group-type'] ?? 'standard-buttons',
                    ...this.mainPlacement,
                }];
            },

            visiblePlacements() {
                return this.backOptions;
            },

            hasEmptySelectionErrorState() {
                return this.isDirty && this.nothingSelected;
            },

            hasPaintfillColorErrorState() {
                return this.isDirty
                    && this.isActive
                    && !this.noCustomizationSelected
                    && !this.customizerStore.isPaintfillComplete('Back');
            },

            allComponents() {
                return this.mainPlacement?.components || [];
            },

            noCustomizationImage() {
                return `PutterBackPaintfill-None-${getImageSuffix(this.viewController)}`;
            },

            isAnyPaintfillSelected() {
                return this.customizerStore.isAnyPaintfillSelected();
            },
        },

        mounted() {
            const group = this.viewController.blueprint.groups.find((x) => x.code === 'BackPaintfill');
            if (group?.custom?.['v-select-view']) {
                this.customizerStore.setActiveView(group?.custom?.['v-select-view']);
            }

            const firstPaintfillOption = this.backOption?.value;

            if (this.isAnyPaintfillSelected && firstPaintfillOption) {
                this.selectActual({
                    option: firstPaintfillOption,
                });
            }
        },

        methods: {
            handleUpdateColorIsSelected() {
            },

            navigateToPaintfill() {
                this.customizerStore.openAllColorLocationsPanel({
                    source: 'CurrentSelection',
                });
            },

            handleSelectNothing() {
                this.customizerStore.resetBack();

                if (this.mainPlacement) {
                    this.customizerStore.syncPaintfillFrom(this.mainPlacement.code);
                }
            },

            handleRemovePaintfillSelection() {
                this.customizerStore.resetAllPaintfill();
                this.closeRemoveConfirmationModal();
            },

            handleSelectNoCustomization() {
                this.handleSelectNothing();
                this.customizerStore.setBackNoCustomizationSelected(true);
            },

            openRemoveConfirmationModal() {
                this.showRemoveConfirmationModal = true;
            },

            closeRemoveConfirmationModal() {
                this.showRemoveConfirmationModal = false;
            },

            selectActual(value) {
                if (this.mainPlacement) {
                    this.customizerStore.setBackNoCustomizationSelected(false);
                    this.customizerStore.setSelection(this.mainPlacement.code, value.option);

                    this.customizerStore.syncPaintfillFrom(this.mainPlacement.code);
                }
            },
        },
    };
</script>
